/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { classNames } from 'primereact/utils';
import { useGlobal } from 'contexts/globalContext';
import { dateFormat } from 'utils/dateFormat';
import { Button } from 'components/_new/Button';
import { useMutation, useQuery } from '@tanstack/react-query';
import { authApi } from 'api';
import { Spinner } from 'components/_new/Spinner';
import { UserAgentInfo } from 'types/Auth';
import { Icon } from 'components/_new/Icon';
import { MessageBox } from 'components/_new/MessageBox';
import { Segment } from './Segment';

type OsIconProps = {
	name: string;
};

const OsIcon = ({ name }: OsIconProps) => {
	useEffect(() => {
		import('../../../assets/ci.min.css');
	}, []);

	const size = 'ci-1x';
	switch (name) {
		case 'Android[-x86]':
		case 'Android':
			return <i className={classNames('ci', size, 'ci-android')} />;
		case 'Arch':
			return <i className={classNames('ci', size, 'ci-archlinux')} />;
		case 'Wndows':
			return <i className={classNames('ci', size, 'ci-ms')} />;
		case 'BlackBerry':
			return <Icon name="blackberry" iconStyle="brand" style={{ color: '#788cb6' }} />;
		case 'CentOS':
			return <i className={classNames('ci', size, 'ci-centos')} />;
		case 'Chromium OS':
			return <i className={classNames('ci', size, 'ci-chromium')} />;
		case 'Fedora':
			return <i className={classNames('ci', size, 'ci-fedora')} />;
		case 'Firefox OS':
			return <i className={classNames('ci', size, 'ci-firefox')} />;
		case 'FreeBSD':
			return <Icon name="blackberry" iconStyle="brand" style={{ color: '#F83C01' }} />;
		case 'Debian':
			return <i className={classNames('ci', size, 'ci-debian')} />;
		case 'Gentoo':
			return <i className={classNames('ci', size, 'ci-gentoo')} />;
		case 'Mac OS':
		case 'iOS':
			return <i className={classNames('ci', size, 'ci-apple')} />;
		case 'Linux':
			return <i className={classNames('ci', size, 'ci-linux')} />;
		case 'RedHat':
			return <i className={classNames('ci', size, 'ci-redhat')} />;
		case 'SUSE':
			return <i className={classNames('ci', size, 'ci-opensuse')} />;
		case 'Ubuntu':
			return <i className={classNames('ci', size, 'ci-ubuntu')} />;
		case 'Unix':
			return <i className={classNames('ci', size, 'ci-unix')} />;
		case 'Windows Phone':
		case 'Windows Mobile':
		case 'Windows':
			return <Icon name="windows" iconStyle="brand" style={{ color: '#0078d7' }} />;
		default:
			return null;
	}
};

type BrowserIconProps = {
	name: string;
};

const BrowserIcon = ({ name }: BrowserIconProps) => {
	const size = 'ci-1x';
	switch (name) {
		case 'Chrome Headless':
		case 'Chrome WebView':
		case 'Chrome':
		case 'Mobile Chrome':
			return <i className={classNames('ci', size, 'ci-chrome')} />;
		case 'Chromium':
			return <i className={classNames('ci', size, 'ci-chromium')} />;
		case 'Edge':
			return <i className={classNames('ci', size, 'ci-edge')} />;
		case 'Electron':
			return <i className={classNames('ci', size, 'ci-electron')} />;
		case 'Facebook':
			return <i className={classNames('ci', size, 'ci-facebook')} />;
		case 'Firefox Focus':
		case 'Firefox Reality':
		case 'Firefox':
			return <i className={classNames('ci', size, 'ci-firefox')} />;
		case 'Instagram':
			return <i className={classNames('ci', size, 'ci-instagram')} />;
		case 'LinkedIn':
			return <i className={classNames('ci', size, 'ci-linkedin')} />;
		case 'Opera':
			return <Icon name="opera" iconStyle="brand" style={{ color: '#cc0f16' }} />;
		case 'Mobile Safari':
		case 'Safari':
			return <i className={classNames('ci', size, 'ci-safari')} />;
		case 'Mozilla':
			return <i className={classNames('ci', size, 'ci-mozilla')} />;
		case 'Snapchat':
			return <i className={classNames('ci', size, 'ci-snapchat')} />;
		case 'TikTok':
			return <i className={classNames('ci', size, 'ci-tiktok')} />;
		case 'Twitter':
			return <i className={classNames('ci', size, 'ci-twitter')} />;
		default:
			return null;
	}
};

const UserAgentDescription = ({ data }: { data: UserAgentInfo }) => {
	const { browser, device, os } = data;

	return (
		<div className="vertical-align-middle inline-flex flex-row align-items-center">
			<span className="inline-flex flex-row gap-1 align-items-center">
				<BrowserIcon name={browser.name} /> {browser.name}
				<span>{browser.version ? ` v${browser.version}` : ''}</span>
			</span>
			{', '}
			<span className="inline-flex flex-row gap-1 align-items-center ml-1">
				{' '}
				<OsIcon name={os.name} /> {os.name}
				<span>{os.version ? ` ${os.version}` : ''}</span>
			</span>
			{device.model || device.vendor ? (
				<span className="inline-flex flex-row gap-1 align-items-center ml-1">{` (${[device.model, device.vendor]
					.filter(Boolean)
					.join(', ')})`}</span>
			) : (
				''
			)}
		</div>
	);
};

export const AuthSessionSection = () => {
	const { t } = useTranslation();
	const { toastRef } = useGlobal();

	const { data, isLoading, isFetched, isSuccess, refetch } = useQuery(
		['ACCOUNT_AUTH_SESSIONS'],
		() => authApi.getActiveAuthSessions(),
		{ enabled: true }
	);

	const lastSessionsCount = 2;
	const hasOthersSessions = (data?.others || []).length > 0;
	const lastSessions = data?.others.slice(0, lastSessionsCount) || [];
	const restSessions = data?.others.slice(lastSessionsCount) || [];
	const [showRestSessions, setShowRestSessions] = useState(false);

	const { mutate, isLoading: mutateLoading } = useMutation(() => authApi.singOutFromAllOtherActiveSessions(), {
		onSuccess: () => {
			refetch();
			toastRef.current?.show({
				severity: 'success',
				life: 5000,
				summary: t('misc.success'),
				detail: t('userPanel.myAccount.activeSessions.signOutSuccessfully'),
			});
		},
	});

	return (
		<>
			{/* <Helmet>
				<link
					rel="stylesheet"
					href="https://cdn.jsdelivr.net/gh/dheereshagrwal/coloured-icons@1.9.4/src/app/ci.min.css"
				/>
			</Helmet> */}
			<Segment title={t('userPanel.myAccount.activeSessions.title')} full>
				<div className="flex flex-column gap-3 w-full">
					{data?.current ? (
						<div className="w-full flex flex-column gap-1">
							<div className="strong">{t('userPanel.myAccount.activeSessions.currentSession')} </div>
							<div className="flex flex-column gap-1">
								<span>
									{t('userPanel.myAccount.activeSessions.description.device')}:{' '}
									<UserAgentDescription data={data?.current.userAgentInfo} />
								</span>
								<span>
									{t('userPanel.myAccount.activeSessions.description.ipAddress')}:{' '}
									{data?.current.ipAddressString}
								</span>
								<span>
									{t('userPanel.myAccount.activeSessions.description.signInDate')}:{' '}
									{dateFormat(data?.current.signIn, 'full')}
								</span>
							</div>
						</div>
					) : (
						'-'
					)}

					{lastSessions.length > 0 ? (
						<div className="flex flex-column gap-2 w-full">
							<div className="strong">
								{t('userPanel.myAccount.activeSessions.otherActiveSessions')}:{' '}
							</div>
							{isLoading && <Spinner center />}
							{isSuccess && isFetched && (
								<div className="flex flex-column gap-1 justify-content-center w-full">
									<div className="flex flex-row gap-1 justify-content-between text-muted text-sm">
										<span>{`${t('userPanel.myAccount.activeSessions.description.browser')}/${t(
											'userPanel.myAccount.activeSessions.description.device'
										)}`}</span>
										<span>
											{t('userPanel.myAccount.activeSessions.description.ipAddress')},{' '}
											{t('userPanel.myAccount.activeSessions.description.lastActiveDate')}
										</span>
									</div>
									<ul className="w-full list-none flex flex-column gap-1">
										{lastSessions.map((authSession) => (
											<li
												key={authSession.id}
												className="flex flex-row gap-1 justify-content-between align-items-center"
											>
												<UserAgentDescription data={authSession.userAgentInfo} />
												<div>
													{authSession.ipAddressString}
													{', '}
													<span>{dateFormat(authSession.lastActivity, 'full')}</span>
												</div>
											</li>
										))}
										{showRestSessions
											? restSessions.map((authSession) => (
													<li
														key={authSession.id}
														className="flex flex-row gap-1 justify-content-between align-items-center"
													>
														<UserAgentDescription data={authSession.userAgentInfo} />
														<div>
															{authSession.ipAddressString}
															{', '}
															<span>{dateFormat(authSession.lastActivity, 'full')}</span>
														</div>
													</li>
												))
											: null}
									</ul>
									{restSessions.length > 0 && !showRestSessions && (
										<Button
											label={t('actions.expand')}
											onClick={() => {
												setShowRestSessions(true);
											}}
											icon="arrow-down"
											variant="primary-text"
											variantSize="sm"
										/>
									)}
								</div>
							)}
							{hasOthersSessions && (
								<div className="flex flex-row justify-content-end">
									<Button
										label={t('userPanel.myAccount.activeSessions.signOutOtherSessions')}
										onClick={() => mutate()}
										variant="primary-outlined"
										variantSize="sm"
										loading={mutateLoading}
									/>
								</div>
							)}
						</div>
					) : (
						<MessageBox
							icon="info-circle"
							iconSet="pi"
							variant="purple"
							message={t('userPanel.myAccount.activeSessions.noOtherSessionsInfo')}
						/>
					)}
				</div>
			</Segment>
		</>
	);
};
